import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {withTranslation} from '../ChildrenContext.js';
import PropTypes from 'prop-types';
import {withRouter} from '../RouterCompatibility';
import moment from 'moment';
import DropdownList from 'react-widgets/DropdownList';
import Custom from '../Multiselects/Custom';
import DatePicker from './DataComponent__DatePicker';
import DynamicSelectSegment from '../DynamicSelectSegment';
import ButtonsList from '../ButtonsList';
import InputGroupMultiselect from '../InputGroupMultiselect';
import CustomNotify from '../Notify';
import Button from '../Button';
import Switch from '../Switch';
import CollapsibleMultiselect from '../Multiselects/CollapsibleMultiselect';
import debounce from '../../helpers/debounce';
import query from '../../helpers/query';
import runtimeConfig from '../../config/runtimeConfig';
import UserStore from '../../stores/UserStore';
import TZ from '../../utils/Timezone/Timezone.js';

const initPage = runtimeConfig.initialPage;
const minLength = 3;
const templateKeys = {
    datepicker: 'getDatepicker',
    timezone: 'getTimezoneDropdown',
    select: 'getSelect',
    multiselect: 'getMultiselect',
    dropdown: 'getDropdown',
    textInput: 'getTextInput',
    custom: 'getCustom',
    dynamic: 'getDynamic',
    goalTree: 'getSelectGoalTree',
    currency: 'getNull',
    switch: 'getSwitch'
};

const FILTER_TYPES_WITHOUT_SEARCH_ICON_IN_LABEL = [
    'datepicker',
    'timezone',
    'switch',
    // collapsible multi-value fields: the label's search icon overlapped their pencil / apply-cancel buttons
    'custom',
    'multiselect',
    'dynamic',
    'goalTree',
];

const getUserTimezone = () => UserStore.getValidTimeZone();
const getNowInUserTimezone = () => moment().tz(getUserTimezone());
const getDefaultStartDate = () => getNowInUserTimezone().subtract(6, 'days').startOf('day');
const getDefaultEndDate = () => getNowInUserTimezone().endOf('day');
const getDefaultPostbackStartDate = () => getNowInUserTimezone().subtract(runtimeConfig.defaultDatepickerDaysPeriod, 'days').startOf('day');

const DataComponentFilter = ({
    fields: propFields,
    location,
    startDate: propStartDate,
    endDate: propEndDate,
    loading: propLoading,
    hideResetButton: propHideResetButton,
    commonControls: propCommonControls = false, // one Apply/Reset pair in a separate row below all filters
    gray: propGray,
    onAction: propOnAction,
    t
}) => {
    const parsedQuery = query.parse(location && location.query);

    const getDefValues = useCallback((fields = []) => {
        let values = {};

        (fields || []).map(item => {
            if (!item.value) return;

            if (item.name === 'datepicker') {
                item.value.startDate && (values[`${item.key}StartDate`] = item.value.startDate);
                item.value.endDate && (values[`${item.key}EndDate`] = item.value.endDate);
                item.value.timezone && (values[`${item.key}Timezone`] = item.value.timezone);
            } else {
                values[item.key] = item.value;
            }
        });

        return values;
    }, []);

    const getSelected = useCallback((fields = [], values = {}) => {
        let selected = [];

        (fields || []).map((item, i) => {
            if (item.disabled || (item.name === 'datepicker' && values[`${item.key}StartDate`])
                || values[item.key]) {
                selected.push({
                    ...item,
                    fieldIndex: i
                });
            }
        });

        return selected;
    }, []);

    const [values, setValues] = useState(() => {
        const defValues = getDefValues(propFields);

        let initialValues = {
            page: parsedQuery.page || initPage,
            ...parsedQuery
        };

        if (!(location && location.pathname) || "/office/finances/report" !== location.pathname) {
            const defaultStartDate = getDefaultStartDate();
            const defaultEndDate = getDefaultEndDate();

            if (!initialValues.createStartDate) {
                initialValues.createStartDate = parsedQuery.createStartDate || parsedQuery.startDate || defValues.createStartDate || propStartDate || defaultStartDate;
            }
            if (!initialValues.createEndDate) {
                initialValues.createEndDate = parsedQuery.createEndDate || parsedQuery.endDate || defValues.createEndDate || propEndDate || defaultEndDate;
            }
        }

        // timezones from the URL the API does not accept (not a whole-hour offset) are replaced
        Object.keys(initialValues).forEach(key => {
            if ((key === 'timezone' || /Timezone$/.test(key)) && initialValues[key]) {
                initialValues[key] = TZ.toSupportedTimezone(initialValues[key]);
            }
        });

        (propFields || []).forEach(field => {
            // standalone timezone filter: always send a value, default to the user's timezone
            if (field && field.name === 'timezone' && field.standalone && !initialValues[field.key]) {
                initialValues[field.key] = TZ.toSupportedTimezone(getUserTimezone());
                return;
            }
            if (field && field.name === 'datepicker' && field.withTimezone) {
                const startDateKey = `${field.key}StartDate`;
                const isSelected = field.disabled || initialValues[startDateKey];
                if (!isSelected) return;

                const timezoneKey = `${field.key}Timezone`;
                if (!initialValues[timezoneKey]) {
                    initialValues[timezoneKey] = TZ.toSupportedTimezone(getUserTimezone());
                }
            }
        });

        Object.keys(defValues).forEach(key => {
            if (!(key in initialValues)) {
                initialValues[key] = defValues[key];
            }
        });

        return initialValues;
    });

    const valuesRef = useRef(values);
    useEffect(() => {
        valuesRef.current = values;
    }, [values]);

    const [selected, setSelected] = useState(() => getSelected(propFields, values));

    const availableFields = useMemo(() => {
        return (propFields || []).filter((field) => field && field.key && !selected.some((selectedItem) => selectedItem.key === field.key));
    }, [propFields, selected]);

    const timezoneOptions = useMemo(() => {
        return (TZ.getAllTimezones() || []).map(tz => ({
            value: tz.key,
            title: tz.nameLong || tz.name || tz.key
        }));
    }, []);

    const staticNames = (t && t.dataTable) || {};
    const staticNamesActions = (t && t.actions) || {};

    const messagesForSelect = {
        emptyList: staticNames.emptyList
    };

    const textInputChange = useCallback(debounce((name) => {
        if (values[name].length > 0 && values[name].length <= minLength) {
            let message = t.validate.minLineLength.replace(/%s/i, minLength);
            CustomNotify(message, 'error');
        }
    }, 500), [values, t]);

    const deleteValue = useCallback((values, item) => {
        if (item.name === 'datepicker') {
            delete values[`${item.key}StartDate`];
            delete values[`${item.key}EndDate`];
            delete values[`${item.key}Timezone`];
        } else {
            delete values[item.key];
        }
    }, []);

    const getValue = useCallback((data) => {
        if (data.name === 'datepicker') {
            const defaultStartDate = getDefaultStartDate();
            const defaultEndDate = getDefaultEndDate();
            const defaultTimezone = getUserTimezone();

            return {
                startDate: values[`${data.key}StartDate`] || defaultStartDate,
                endDate: values[`${data.key}EndDate`] || defaultEndDate,
                timezone: values[`${data.key}Timezone`] || values.timezone || defaultTimezone,
            };
        } else if (data.name === 'timezone') {
        const defaultTimezone = getUserTimezone();
            return values[data.key] || defaultTimezone;
        } else if (data.name === 'textInput') {
            return values[data.key] || '';
        } else if (data.name === 'dynamic') {
            return values[data.key] || [];
        }

        return values[data.key];
    }, [values, location]);

    const getNull = useCallback(() => {
        return null;
    }, []);

    const onDataPickerApply = useCallback((datepicker, key) => {
        // Resolve start/end of day in the field's timezone (not the browser's).
        const fieldTimezone = valuesRef.current[`${key}Timezone`] || valuesRef.current.timezone || getUserTimezone();

        const startDate = moment.tz(
            datepicker.startDate.format(runtimeConfig.codeFormat),
            runtimeConfig.codeFormat,
            fieldTimezone
        ).startOf('day');
        const endDate = moment.tz(
            datepicker.endDate.format(runtimeConfig.codeFormat),
            runtimeConfig.codeFormat,
            fieldTimezone
        ).endOf('day');

        setValues(prevValues => ({
            ...prevValues,
            [`${key}StartDate`]: startDate,
            [`${key}EndDate`]: endDate
        }));
    }, []);

    const onTextInputChange = useCallback((event) => {
        const name = event.target.name;

        setValues(prevValues => ({
            ...prevValues,
            [name]: event.target.value
        }));

        textInputChange(name);
    }, [textInputChange]);

    const onSelectChange = useCallback((event) => {
        const name = event.target.name;

        setValues(prevValues => ({
            ...prevValues,
            [name]: event.target.value
        }));
    }, []);

    const onChange = useCallback((value, key) => {
        setValues(prevValues => ({
            ...prevValues,
            [key]: value
        }));
    }, []);

    const onChangeDynamic = useCallback((value, key) => {
        const newValue = (value || []).map(item => {return {title: item.title, value: item.value}});

        setValues(prevValues => {
            return {
                ...prevValues,
                [key]: newValue
            };
        });
    }, []);

    const onAction = useCallback((type, customValues) => {
        const baseValues = customValues ? { ...customValues } : { ...valuesRef.current };
        const stateData = {
            ...baseValues,
            page: initPage
        };

        setValues(stateData);
        propOnAction(stateData, type);
    }, [propOnAction]);

    const onChangeFilter = useCallback((item, index) => {
        const fieldIndex = propFields.findIndex(data => data.key === item.key);
        let newSelected = [...selected];
        let newValues = {...values};

        if (index >= 0 && index < newSelected.length) {
            let oldItem = newSelected[index];
            deleteValue(newValues, oldItem);

            const existingIndex = newSelected.findIndex((s, idx) => s && s.key === item.key && idx !== index);
            if (existingIndex >= 0) {
                let existingItem = newSelected[existingIndex];
                deleteValue(newValues, existingItem);
                newSelected.splice(existingIndex, 1);
                if (existingIndex < index) {
                    index--;
                }
            }

            newSelected[index] = {
                ...item,
                fieldIndex
            };
        } else {
            const existingIndex = newSelected.findIndex(s => s && s.key === item.key);
            if (existingIndex >= 0) {
                let oldItem = newSelected[existingIndex];
                deleteValue(newValues, oldItem);
                newSelected[existingIndex] = {
                    ...item,
                    fieldIndex
                };
            } else {
                newSelected.push({
                    ...item,
                    fieldIndex
                });
            }
        }

        setSelected(newSelected);
        setValues(newValues);
    }, [selected, values, propFields, deleteValue]);

    const onAddFilter = useCallback((item) => {
        const fieldIndex = propFields.findIndex(data => data.key === item.key);
        const nextSelected = [...selected, { ...item, fieldIndex }];
        setSelected(nextSelected);

        try {
            const isDatepicker = item && item.name === 'datepicker';
            if (isDatepicker) {
                const nextValues = {
                    ...values,
                    [`${item.key}StartDate`]: values[`${item.key}StartDate`] || getDefaultPostbackStartDate(),
                    [`${item.key}EndDate`]: values[`${item.key}EndDate`] || getDefaultEndDate(),
                    page: initPage
                };
                if(item.withTimezone) {
                    nextValues[`${item.key}Timezone`] = values[`${item.key}Timezone`] || getUserTimezone();
                }
                setValues(nextValues);
                propOnAction(nextValues, 'filtersChange');
            }
        } catch (e) {}
    }, [selected, propFields, location, values, propOnAction]);

    const onDelete = useCallback((item, index) => {
        const newSelected = [...selected];
        const newValues = { ...values };

        const targetIndex = index !== undefined && index !== null
            ? index
            : newSelected.findIndex((entry) => entry && entry.key === item.key && entry.fieldIndex === item.fieldIndex);

        if (targetIndex < 0 || targetIndex >= newSelected.length) {
            return;
        }

        const deletedItem = newSelected[targetIndex];
        deleteValue(newValues, deletedItem);
        newSelected.splice(targetIndex, 1);
        setSelected(newSelected);
        setValues(newValues);
        onAction('reset', newValues);
    }, [selected, values, deleteValue, onAction]);

    // commonControls mode: remove an added filter from the form only; the request is sent by the common Apply
    const onRemoveFilter = useCallback((item) => {
        const newSelected = [...selected];
        const newValues = { ...values };
        const targetIndex = newSelected.findIndex((entry) => entry && entry.key === item.key);

        if (targetIndex < 0) {
            return;
        }

        deleteValue(newValues, newSelected[targetIndex]);
        newSelected.splice(targetIndex, 1);
        setSelected(newSelected);
        setValues(newValues);
    }, [selected, values, deleteValue]);

    useEffect(() => {
        onAction('firstRender');
    }, [onAction]);

    const getDatepicker = useCallback((data) => {
        const onDataPickerApplyCallback = (item) => onDataPickerApply(item, data.key);

        return (
            <>
                <DatePicker
                    onApply={onDataPickerApplyCallback}
                    timezone={getValue({ ...data }).timezone}
                    className="btn form-control reactdaypicker-block"
                    {...getValue(data)}
                />
                { data.withTimezone ? (
                    getTimezoneDropdown(data)
                ): null }
            </>
        );
    }, [onDataPickerApply, getValue, getTimezoneDropdown]);

    // value: 1 when on, null when off (off is not sent and not kept in the URL)
    const getSwitch = useCallback((data) => {
        return (
            <div className="table-filter__switch">
                <Switch
                    checked={Boolean(values[data.key])}
                    onChange={(checked) => { onChange(checked ? 1 : null, data.key); }}
                    onText={staticNamesActions.yes || 'Yes'}
                    offText={staticNamesActions.no || 'No'}
                />
            </div>
        );
    }, [values, onChange, staticNamesActions]);

    const getSelect = useCallback((select) => {
        return (
            <select
                onChange={onSelectChange}
                className="form-control long-input right"
                name={select.key}
                value={getValue(select)}
            >
                {(propFields[select.fieldIndex].data || []).map((option, i) => (
                    <option key={i} value={option.value}>{option.title}</option>
                ))}
            </select>
        );
    }, [onSelectChange, getValue, propFields]);

    const getDropdown = useCallback((dropdown) => {
        return (
            <DropdownList
                messages={messagesForSelect}
                className={'rw-dropdownlist-select'}
                dataKey="value"
                textField='title'
                placeholder={dropdown.placeholder || ''}
                data={propFields[dropdown.fieldIndex].data}
                value={getValue(dropdown)}
                onChange={(value) => { onChange(value, dropdown.key); }}
            />
        );
    }, [messagesForSelect, getValue, propFields, onChange]);

    const getTimezoneDropdown = useCallback((data) => {
        const timezoneKey = data.standalone ? data.key : `${data.key}Timezone`;

        const val = getValue({ ...data });

        return (
            <DropdownList
                messages={messagesForSelect}
                className={'rw-dropdownlist-select'}
                dataKey="value"
                textField='title'
                placeholder={data.placeholder || ''}
                data={timezoneOptions}
                value={data.standalone ? val : val.timezone}
                onChange={(item) => { onChange(item && item.value, timezoneKey); }}
                filter='contains'
            />
        );
    }, [messagesForSelect, getValue, onChange, timezoneOptions]);

    const getMultiselect = useCallback((select) => {
        return (
            <CollapsibleMultiselect
                messages={messagesForSelect}
                placeholder={select.placeholder || ''}
                className='right'
                dataKey="value"
                textField='title'
                onChange={(value) => { onChange(value, select.key); }}
                data={propFields[select.fieldIndex].data}
                name={select.key}
                value={getValue(select)}
            />
        );
    }, [messagesForSelect, onChange, propFields, getValue]);

    const getTextInput = useCallback((data) => {
        return (
            <input
                className="form-control"
                type="text"
                name={data.key}
                onChange={onTextInputChange}
                placeholder={data.placeholder}
                value={getValue(data)}
            />
        );
    }, [onTextInputChange, getValue]);

    const getCustom = useCallback((data) => {
        const onChangeCustom = (item) => onChange(item, data.key);

        return (
            <Custom
                segment={data.label}
                values={getValue(data)}
                onAction={onChangeCustom}
                numericOnly={Boolean(data.numericOnly)}
            />
        );
    }, [onChange, getValue]);

    const getDynamic = useCallback((data) => {
        const onChangeDynamicCallback = (item) => onChangeDynamic(item, data.key);

        // collapsing is built into DynamicSelectSegment (multiselect mode)
        return (
            <DynamicSelectSegment
                segment={data.segment}
                multiselect={true}
                values={getValue(data)}
                onAction={onChangeDynamicCallback}
            />
        );
    }, [onChangeDynamic, getValue]);

    const getSelectGoalTree = useCallback((data) => {
        const onChangeCallback = (item) => onChange(item, data.key);

        return (
            <InputGroupMultiselect
                nesting={data.nesting}
                onChange={onChangeCallback}
                tags={getValue(data)}
            />
        );
    }, [onChange, getValue]);

    const getChangeFunction = useCallback((value, key, name) => {
        if(name === 'currency' ||  key === 'currency') {
            return onChange(value, key);
        } else if(name === 'datepicker') {
            return onDataPickerApply(value, key);
        } else {
            alert('No function assigned');
        }
    }, [onChange, onDataPickerApply]);

    const getNumberOfActiveFilters = useCallback(() => {
        return selected.filter(s => s && s.key && s.name).length;
    }, [selected]);

    const renderFilter = useCallback((item = {}, i) => {
        const onChangeFilterCallback = (item) => onChangeFilter(item, i);
        const onChangeDropdown = (i >= 0) ? onChangeFilterCallback : onAddFilter;
        const onDeleteCallback = () => onDelete(item, i);
        const numberOfActiveFilters = getNumberOfActiveFilters();
        const canDelete = numberOfActiveFilters > 1;
        const isDateOfCreation = item && item.name === 'datepicker' && item.key === 'create';
        const isTimezone = item && item.name === 'timezone' && item.key === 'timezone';
        const canDeleteFilter = canDelete && !isDateOfCreation && !isTimezone;

        const selectedOption = (item && item.key)
            ? (propFields.find(f => f && f.key === item.key) || { key: item.key, label: item.label })
            : null;

        const dropdownOptions = (() => {
            if (!Array.isArray(propFields) || !propFields.length) return [];
            if (i >= 0 && selectedOption) {
                const rest = (propFields || []).filter((f) => f && f.key && f.key !== selectedOption.key && !selected.some(s => s && s.key === f.key));
                return [selectedOption, ...rest];
            }
            return (propFields || []).filter((f) => f && f.key && !selected.some(s => s && s.key === f.key));
        })();

        const dropdownValue = item && item.key ? selectedOption : null;

        const key = item && item.key ? item.key : (i >= 0 ? `filter-${i}` : 'filter-new');

        return (
            <div className={`table-filter ${propGray ? ' table-filter-gray' : ''}`}  key={key}>
                {item.withoutDropdown
                    ? null
                    : (<div className="table-filter__item">
                        <label> {item && item.dropdownLabel ? item.dropdownLabel : staticNames.addFilter} </label>
                        <DropdownList
                            className={'rw-dropdownlist-select'}
                            placeholder={staticNames.addFilter }
                            dataKey="key"
                            textField='label'
                            data={dropdownOptions}
                            disabled={Boolean(item.disabled) || dropdownOptions.length === 0}
                            value={dropdownValue}
                            onChange={onChangeDropdown}
                        />
                    </div>)
                }
                {item.name
                    ? (
                        <React.Fragment>
                            <div className="table-filter__item payouts-block">
                                {item.label && <label> {item.label}
                                    {!(FILTER_TYPES_WITHOUT_SEARCH_ICON_IN_LABEL.includes(item.name)) ? (<i className="fa fa-search"></i>) : null}
                                </label>}
                                {templateKeys[item.name] && (() => {
                                    const methodName = templateKeys[item.name].replace('get', '');
                                    switch(methodName) {
                                        case 'Datepicker': return getDatepicker(item);
                                        case 'TimezoneDropdown': return getTimezoneDropdown(item);
                                        case 'Select': return getSelect(item);
                                        case 'Multiselect': return getMultiselect(item);
                                        case 'Dropdown': return getDropdown(item);
                                        case 'TextInput': return getTextInput(item);
                                        case 'Custom': return getCustom(item);
                                        case 'Dynamic': return getDynamic(item);
                                        case 'SelectGoalTree': return getSelectGoalTree(item);
                                        case 'Switch': return getSwitch(item);
                                        case 'Null': return getNull();
                                        default: return null;
                                    }
                                })()}
                            </div>
                            {!propCommonControls && (!item.disabled || ['datepicker', 'timezone'].includes(item.name))
                                ? (
                                    <div className="table-filter__item control-buttons">
                                        <Button
                                           loading={propLoading}
                                           extraClass={{'apply-btn': true}}
                                           text={staticNamesActions.apply}
                                           onClick={onAction}
                                       />
                                        <Button
                                            extraClass={{'reset-btn': true, 'hidden': propHideResetButton}}
                                            text={staticNamesActions.reset}
                                            onClick={onDeleteCallback}
                                            disabled={!canDeleteFilter || !item.key}
                                        />
                                    </div>
                                )
                                : null
                            }
                            {propCommonControls && item.key && !item.disabled
                                ? (
                                    <div className="table-filter__item table-filter__item--remove">
                                        <button
                                            type="button"
                                            className="btn reset-btn table-filter__remove"
                                            title={staticNames.removeFilter || 'Remove filter'}
                                            aria-label={staticNames.removeFilter || 'Remove filter'}
                                            onClick={() => onRemoveFilter(item)}
                                        >
                                            &times;
                                        </button>
                                    </div>
                                )
                                : null
                            }
                        </React.Fragment>
                    )
                    : null
                }
                {item.withList
                    ? <ButtonsList
                        fields={propFields}
                        data={item}
                        onChange={(value, key) => getChangeFunction(value, key, item.name)}
                        currentValue={values[item.key]}
                        showSubtitle={item.showSubtitle}
                       />
                    : null
                }
            </div>
        );
    }, [propGray, staticNames, propFields, selected, propLoading, staticNamesActions, propHideResetButton, onAction, onDelete, onChangeFilter, onAddFilter, getChangeFunction, values, availableFields, getNumberOfActiveFilters, getSwitch, propCommonControls, onRemoveFilter]);

    // Reset all filters to their initial state: only always-visible (disabled) fields stay, with default values
    const onResetAll = useCallback(() => {
        const newValues = {
            ...getDefValues(propFields),
            page: initPage
        };

        if (!(location && location.pathname) || "/office/finances/report" !== location.pathname) {
            newValues.createStartDate = newValues.createStartDate || getDefaultStartDate();
            newValues.createEndDate = newValues.createEndDate || getDefaultEndDate();
        }

        (propFields || []).forEach(field => {
            if (!field) return;

            if (field.name === 'timezone' && field.standalone && !newValues[field.key]) {
                newValues[field.key] = getUserTimezone();
            }
            if (field.name === 'datepicker' && field.withTimezone
                && (field.disabled || newValues[`${field.key}StartDate`])
                && !newValues[`${field.key}Timezone`]) {
                newValues[`${field.key}Timezone`] = getUserTimezone();
            }
        });

        setSelected(getSelected(propFields, newValues));
        setValues(newValues);
        onAction('reset', newValues);
    }, [propFields, location, getDefValues, getSelected, onAction]);

    const renderCommonControls = () => (
        <div className="table-filter table-filter__controls">
            <div className="table-filter__item control-buttons">
                <Button
                    loading={propLoading}
                    extraClass={{'apply-btn': true}}
                    text={staticNamesActions.apply}
                    onClick={onAction}
                />
                <Button
                    extraClass={{'reset-btn': true, 'hidden': propHideResetButton}}
                    text={staticNamesActions.reset}
                    onClick={onResetAll}
                />
            </div>
        </div>
    );

    if (!propFields.length) return null;

    return (
        <div className="ibox-title">
            {(selected || []).map( filter => renderFilter(filter))}
            {availableFields.length
                ? renderFilter()
                : null}
            {propCommonControls ? renderCommonControls() : null}
        </div>
    );
};

DataComponentFilter.propTypes = {
    fields: PropTypes.array,
    t: PropTypes.object
};

DataComponentFilter.defaultProps = {
    fields: []
};

export default withRouter(withTranslation(DataComponentFilter));
